import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, Stack, Tab, Tabs, Typography } from '@mui/material';
import { ArrowBack, ArrowForward, CalendarMonth, Category, Check } from '@mui/icons-material';
import { endOfDay, startOfDay } from 'date-fns';

import { ctxTachographDiagram, TachographDiagramContext } from '@/contexts/TachographDiagramCtx';
import { Popup } from '@/components/common/Popup';
import { DateRangeSelect } from '@/components/common/date/DateRangeSelect';
import { SingleTargetSelector } from '@/components/common/SingleTargetSelector';
import { useTachographDataProcessing } from '@/hooks/useTachographDataProcessing';
import { datePeriodMD } from '@/utilities/dateUtils';
import { TabValue } from '@/types';
import { DataView, EmptyView, IdleView, LoadingView } from './TachographComponents';
import { TachographMobileBody } from './TachographMobileBody';

import './TachographDiagram.css';

function TachographDiagramHeaderTitle({ showDescription }: { showDescription?: boolean }) {
  const ctx = useContext(ctxTachographDiagram);
  const { t } = useTranslation();
  return (
    <>
      <Stack direction="row" spacing={1}>
        <Typography variant="h6">{t('tachographDiagram.diagramTitle')}</Typography>
        <Typography variant="h6" color="text.secondary">
          ({ctx?.selectedTarget?.name ?? ''})
        </Typography>
      </Stack>
      {showDescription && (
        <Typography variant="helperText" whiteSpace="pre-line">
          {t('tachographDiagram.diagramExplanation')}
        </Typography>
      )}
    </>
  );
}

function TachographDiagramPanel() {
  const ctx = useContext(ctxTachographDiagram);
  const tachographProcessedData = useTachographDataProcessing();

  if (!ctx) return;

  return (
    <Box flex={1} overflow="auto" display="flex" flexDirection="column">
      <Box sx={{ pl: 2, pt: 2, boxShadow: '0 4px 6px rgb(0 0 0 / 20%)', zIndex: 1 }}>
        <TachographDiagramHeaderTitle showDescription />
      </Box>
      {ctx.status === 'idle' && <IdleView />}
      {ctx.status === 'empty' && <EmptyView />}
      {ctx.status === 'loading' && <LoadingView />}
      {ctx.status === 'data' && <DataView tachographProcessedData={tachographProcessedData} />}
    </Box>
  );
}

export function TachographDesktop() {
  const ctx = useContext(ctxTachographDiagram);
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState<string | undefined>(undefined);

  function onAccept(newFrom: Date | undefined, newTo: Date | undefined) {
    if (!newFrom || isNaN(newFrom.getTime()) || !newTo || isNaN(newTo.getTime())) {
      ctx?.setSelectedDates(undefined);
      return;
    }
    const from = startOfDay(newFrom);
    const to = endOfDay(newTo);
    ctx?.setSelectedDates({ from, to });
  }

  if (!ctx) return;

  return (
    <Box width={'100%'} display={'flex'} pl={'10px'}>
      <div className="tachograph-diagram-selectors-container">
        <Box sx={{ width: '470px', py: 2, display: 'flex', alignItems: 'center' }}>
          <Stack>
            <Typography variant="h6" sx={{ width: '200px' }}>
              {t('tachograph.dateSelector.title')}
            </Typography>
            <Typography variant="helperText" sx={{ maxWidth: '200px' }}>
              {t('tachograph.dateSelector.description')}
            </Typography>
          </Stack>
          <DateRangeSelect setSelectedDates={onAccept} currentValue={ctx.selectedDates || { from: null, to: null }} />
        </Box>
        <div className={`tachograph-diagram-target-selector`}>
          <SingleTargetSelector
            searchText={searchText}
            setSearchText={setSearchText}
            selectedTarget={ctx.selectedTarget}
            setSelectedTarget={ctx.setSelectedTarget}
            tachographFilter={true}
          />
        </div>
      </div>
      <TachographDiagramPanel />
    </Box>
  );
}

function TachographMobileHeader() {
  const ctx = useContext(ctxTachographDiagram);
  const { t } = useTranslation();

  const hasDates = Boolean(ctx?.selectedDates?.from && ctx.selectedDates?.to);
  const intervalLength = useMemo(() => {
    if (!ctx) return '';
    if (hasDates) {
      return datePeriodMD(ctx.selectedDates!.from, ctx.selectedDates!.to, t);
    }
    return '';
  }, [ctx?.selectedDates?.from, ctx?.selectedDates?.to, hasDates]);

  if (!ctx) return null;

  const onResultTab = ctx.tab === TabValue.result;
  const canGoForward = hasDates && !onResultTab; // && activeTab !== 'undefined';

  function navigateBack() {
    ctx?.setTab(ctx.tab - 1);
  }

  function navigateForward() {
    ctx?.setTab(TabValue.result);
  }

  return (
    <>
      <Stack direction="row" sx={{ alignItems: 'center' }}>
        {onResultTab && (
          <IconButton data-qa-element-id="back_button" onClick={navigateBack} sx={{ color: 'text.primary' }}>
            <ArrowBack />
          </IconButton>
        )}
        {ctx.selectedTarget && onResultTab ? (
          <TachographDiagramHeaderTitle />
        ) : (
          <Typography>{t('tachographDiagram.diagramTitle')}</Typography>
        )}
        {canGoForward && (
          <Box sx={{ flex: 1, textAlign: 'right' }}>
            <IconButton data-qa-element-id="forward_button" onClick={navigateForward} sx={{ color: 'text.primary' }}>
              <ArrowForward />
            </IconButton>
          </Box>
        )}
      </Stack>
      {!onResultTab && (
        <Box>
          <Tabs
            variant="fullWidth"
            value={ctx.tab}
            onChange={(_, value) => ctx.setTab(value as TabValue)}
            sx={{
              indicatorColor: 'secondary',
              textColor: 'secondary',
            }}
          >
            <Tab
              data-qa-element-id="date_selector_tab"
              value={TabValue.targetSelect}
              icon={
                <Box>
                  <CalendarMonth />
                  {ctx.selectedDates && (
                    <Check sx={{ color: 'success', fontSize: 'medium', position: 'absolute', right: -25 }} />
                  )}
                </Box>
              }
              label={
                <div
                  data-qa-element-id={`date_selector_tab_label_${ctx.selectedDates?.from.toLocaleDateString()}_${ctx.selectedDates?.to.toLocaleDateString()}`}
                >
                  {intervalLength || t('refuels.period')}
                </div>
              }
              sx={{ textTransform: 'none' }}
            />
            <Tab
              data-qa-element-id="category_selector_tab"
              value={TabValue.dateSelect}
              icon={<Category />}
              label={t('refuels.selectVehicle')}
              sx={{ textTransform: 'none' }}
            />
          </Tabs>
        </Box>
      )}
    </>
  );
}

function TachographMobilePopup({ closePopup }: { closePopup: () => Promise<void> }) {
  const ctx = useContext(ctxTachographDiagram);
  const atDataView = ctx?.tab === TabValue.result && ctx?.status === 'data';
  return (
    <Popup
      popupTitle={undefined}
      header={<TachographMobileHeader />}
      body={<TachographMobileBody />}
      footer={undefined}
      closePopup={closePopup}
      isFullScr={true}
      showCloseButton={false}
      additionalClassName={atDataView ? 'tachograph-diagram-gray-background' : ''}
      scrollUnderHeaderView={atDataView}
    />
  );
}

export function TachographDiagramPopup({
  closePopup,
  isBottom,
}: {
  closePopup: () => Promise<void>;
  isBottom: boolean;
}) {
  return (
    <TachographDiagramContext>
      {isBottom ? <TachographMobilePopup closePopup={closePopup} /> : <TachographDesktop />}
    </TachographDiagramContext>
  );
}
